import React, { useEffect, useRef, useState } from "react";
import WelcomeSplash, { splashMedia } from "../components/WelcomeSplash";
import { useParams, Link } from "react-router-dom";
import { usePortfolios, fixMedia } from "../context/PortfolioContext";
import { apiGetPortfolio } from "../api";
import PortfolioRenderer from "../components/PortfolioRenderer";
import AnimatedWaves from "../components/AnimatedWaves";
import { Lock, ArrowLeft } from "lucide-react";
import { primeMusic, findBgMusic } from "../lib/bgMusic";

const cacheKey = (slug) => `pf-cache:${slug}`;
const readCache = (slug) => {
  try {
    const raw = localStorage.getItem(cacheKey(slug));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

// Carga SOLO el portafolio abierto; muestra al instante la copia guardada y la actualiza.
export const usePublicPortfolio = (slug) => {
  const [data, setData] = useState(() => readCache(slug));
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    let timer;
    setData(readCache(slug));
    setNotFound(false);
    const load = async () => {
      try {
        const p = fixMedia(await apiGetPortfolio(slug));
        if (!alive) return;
        setData(p);
        try {
          localStorage.setItem(cacheKey(slug), JSON.stringify(p));
        } catch (e) {}
      } catch (e) {
        if (!alive) return;
        if (e?.response?.status === 404) {
          setNotFound(true);
          localStorage.removeItem(cacheKey(slug));
        } else timer = setTimeout(load, 2500);
      }
    };
    load();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [slug]);

  return { data, notFound };
};

const Screen = ({ children }) => (
  <div className="pf-screen relative flex items-center justify-center px-6 text-center">
    <AnimatedWaves />
    <div className="relative z-10 flex flex-col items-center">{children}</div>
  </div>
);

const SPLASH_MS = 2000;
const EXIT_MS = 700;

// Fases de la entrada: "show" (logo) → "exit" (transformación) → "done".
const useSplash = (slug, portfolio, notFound) => {
  const seenKey = `pf-splash:${slug}`;
  const [phase, setPhase] = useState(() => (sessionStorage.getItem(seenKey) ? "done" : "show"));
  const start = useRef(Date.now());
  useEffect(() => {
    if (phase !== "show") return;
    if (notFound || (portfolio && portfolio.splash === false)) return setPhase("done");
    if (!portfolio) return;
    // El logo aparece cuando llegan los datos: se muestra al menos 1.6 s y en total ≥ 2 s.
    const wait = Math.max(1600, SPLASH_MS - (Date.now() - start.current));
    const t = setTimeout(() => setPhase("exit"), wait);
    return () => clearTimeout(t);
  }, [phase, portfolio, notFound]);
  useEffect(() => {
    if (phase !== "exit") return;
    sessionStorage.setItem(seenKey, "1");
    const t = setTimeout(() => setPhase("done"), EXIT_MS);
    return () => clearTimeout(t);
  }, [phase, seenKey]);
  return phase;
};

const PortfolioView = () => {
  const { slug } = useParams();
  const { getPortfolio, ready } = usePortfolios();
  const { data, notFound } = usePublicPortfolio(slug);
  const portfolio = (ready && getPortfolio(slug)) || data;
  const phase = useSplash(slug, portfolio, notFound);
  const splashed = useRef(phase !== "done");
  // Al terminar la entrada se quita la clase: un transform/filter activo rompe el fondo fijo.
  const [entering, setEntering] = useState(splashed.current);
  const bg = portfolio?.background || {};
  const musicUrl = portfolio?.status === "suspended" ? null : findBgMusic(portfolio?.blocks);
  // Precarga la música durante el logo; cualquier toque la desbloquea.
  useEffect(() => {
    if (musicUrl) primeMusic(musicUrl);
  }, [musicUrl]);
  const splash = phase !== "done" && (
    <WelcomeSplash
      {...splashMedia(portfolio)}
      pending={!portfolio}
      glow={bg.glow}
      sound={!!portfolio?.splashSound}
      exiting={phase === "exit"}
    />
  );

  if (phase === "show") return splash;

  if (!portfolio && !notFound) {
    return (
      <Screen>
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white" />
        <p className="mt-3 text-sm text-white/70" data-testid="portfolio-loading">Cargando perfil…</p>
      </Screen>
    );
  }

  if (!portfolio) {
    return (
      <Screen>
        <h1 className="font-display text-3xl font-bold text-white">404</h1>
        <p className="mt-2 text-white/60">Este portafolio no existe.</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-500"
        >
          <ArrowLeft className="h-4 w-4" /> Ir al panel
        </Link>
      </Screen>
    );
  }

  if (portfolio.status === "suspended") {
    return (
      <Screen>
        <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10">
          <Lock className="h-7 w-7 text-blue-300" />
        </span>
        <h1 className="mt-5 font-display text-2xl font-bold text-white">Perfil suspendido</h1>
        <p className="mt-2 max-w-sm text-sm text-white/60">
          Este portafolio no está disponible temporalmente. Contacta al administrador para
          reactivarlo.
        </p>
      </Screen>
    );
  }

  return (
    <>
      <div
        className={`pf-screen ${entering ? "pf-enter" : ""}`}
        onAnimationEnd={(e) => e.target === e.currentTarget && setEntering(false)}
        data-testid="portfolio-view"
      >
        <PortfolioRenderer portfolio={portfolio} animate />
      </div>
      {splash}
    </>
  );
};

export default PortfolioView;
